import { cn, type SectionModeType } from '@blogging/utils'
import * as React from 'react'

export interface ImageProps {
  src: string
  alt?: string
  title?: string
  width?: string | number
  height?: string | number
  mode?: SectionModeType
  className?: string
}

/**
 * CMS Image component for rendering images with mode support (normal/wide/full)
 * Pure presentation component used by both editor (preview) and site (rendering)
 *
 * Uses CSS Grid placement via data-width attribute for article grid layout:
 * - normal: content width (default text column)
 * - wide: wider container for larger images
 * - full: full browser width
 *
 * All Tailwind classes are inlined directly in this source file for automatic detection
 * by the consuming app's Tailwind JIT compiler.
 */
export const Image = React.forwardRef<HTMLDivElement, ImageProps>(
  ({ src, alt, title, width, height, mode = 'normal', className }, ref) => {
    // Margin classes - all variants must be present as complete strings
    const marginClassMap = {
      full: 'my-12',
      wide: 'my-8',
      normal: 'my-6',
    }

    // Image classes - all variants must be present as complete strings
    const imageClassMap = {
      full: 'block w-full',
      wide: 'block w-full',
      normal: 'block w-full max-w-full',
    }

    const marginClasses = marginClassMap[mode]
    const imageClasses = imageClassMap[mode]

    return (
      <div
        ref={ref}
        className={cn('not-prose', marginClasses, className)}
        data-width={mode}
      >
        <img
          src={src}
          alt={alt || ''}
          title={title}
          width={width ? Number(width) : undefined}
          height={height ? Number(height) : undefined}
          className={imageClasses}
          loading="lazy"
        />
      </div>
    )
  },
)

Image.displayName = 'Image'
